/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, DepartmentDTO, PeopleDTO, PeopleFilterDTO, UserDTO, UserFilterDTO, ValueListDTO, ValueListFilterDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface IDepartmentMaintenanceProps {
    data: any;

    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const DepartmentMaintenance: React.FC<IDepartmentMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [responsibleList, setResposibleList] = useState<PeopleDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [departmentDTO, setDepartmentDTO] = useState<DepartmentDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setDepartmentDTO({
            ...departmentDTO,
            id: '',
            name: '',
            code : '',
            description: '',
            responsibleId: '',
            companyId: ''
        } as DepartmentDTO);

        if (isOpenMaintenance) {
            handleCompanyGetAll();
            handlerUserGetAllAsync();
            handleGetDepartmentByIdAsync();

            handleGetPermission(ListId.corpsphere.departmentId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        handleResposibleGetAll();
    }, [departmentDTO?.companyId]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleResposibleGetAll = useCallback(async () => {
        const peopleFilterDTO: PeopleFilterDTO = {
            companyId: departmentDTO?.companyId,
            isServiceAccount: false,
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.people', peopleFilterDTO, setLoading, setResposibleList, showMessage);
    }, [departmentDTO?.companyId]);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handlerUserGetAllAsync = useCallback(async () => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleGetDepartmentByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('corpsphere.department', data.id, setLoading, setDepartmentDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = departmentDTO as DepartmentDTO;

        if (departmentDTO?.id === '' || departmentDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('corpsphere.department', dto, setDepartmentDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage('There is no permission for this access.', 'info');
                return;
            }
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('corpsphere.department', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [departmentDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: t('department.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: t('department.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('department.detailsName')}
                icon='group_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={10}>
                            <TextFieldInput
                                id='name'
                                label={t('department.maintenance.name')}
                                value={departmentDTO?.name}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                required={true}
                                maxLength={200}
                                autoFocus={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <TextFieldInput
                                id='code'
                                label={t('department.maintenance.code')}
                                value={departmentDTO?.code}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                required={true}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='companyId'
                                label={t('department.maintenance.company')}
                                placeholder='Company'
                                value={departmentDTO?.companyId}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                valueList={companyList}
                                required={true}
                                hasNoneSelect={false}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='responsibleId'
                                label={t('department.maintenance.responsible')}
                                placeholder='Responsable'
                                value={departmentDTO?.responsibleId}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                valueList={responsibleList}
                                required={true}
                                hasNoneSelect={false}
                                searchable={true}
                                labelKey='fullName'
                                valueKey='id'
                            />
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextFieldInput
                                id='teamLink'
                                label={t('department.maintenance.teamLink')}
                                value={departmentDTO?.teamLink}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                required={false}
                                maxLength={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='contactEmail'
                                label={t('department.maintenance.contactEmail')}
                                value={departmentDTO?.contactEmail}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                required={true}
                                maxLength={400}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label={t('department.maintenance.description')}
                                value={departmentDTO?.description}
                                setValue={setDepartmentDTO}
                                obj={departmentDTO}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {departmentDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label={t('department.maintenance.id')}
                                        value={departmentDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('department.maintenance.createdBy')}
                                        value={userList.find(x => x.id === departmentDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('department.maintenance.creationDateTime')}
                                        value={formatDateTime(String(departmentDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('department.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === departmentDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('department.maintenance.updateDateTime')}
                                        value={formatDateTime(String(departmentDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};